import { Check, Package, Truck, Home, XCircle } from 'lucide-react';
import { motion } from 'framer-motion';

type OrderStatus = 'processing' | 'shipped' | 'delivered' | 'cancelled';

interface OrderTimelineProps {
  status: OrderStatus;
  orderDate: string;
}

const steps = [
  { key: 'processing', label: 'Order Placed', icon: Package },
  { key: 'shipped', label: 'Shipped', icon: Truck },
  { key: 'delivered', label: 'Delivered', icon: Home },
];

export function OrderTimeline({ status, orderDate }: OrderTimelineProps) {
  if (status === 'cancelled') {
    return (
      <div className="flex items-center gap-3 py-3 px-4 bg-destructive/10 rounded-lg">
        <div className="w-8 h-8 rounded-full bg-destructive/20 flex items-center justify-center">
          <XCircle className="h-4 w-4 text-destructive" />
        </div>
        <div>
          <p className="font-medium text-destructive">Order Cancelled</p>
          <p className="text-xs text-muted-foreground">
            Cancelled on {new Date(orderDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
          </p>
        </div>
      </div>
    );
  }

  const currentStepIndex = steps.findIndex(s => s.key === status);

  return (
    <div className="py-4 px-4">
      <div className="relative flex items-center justify-between">
        {/* Progress line */}
        <div className="absolute top-4 left-4 right-4 h-0.5 bg-secondary" />
        <motion.div
          className="absolute top-4 left-4 h-0.5 bg-discount"
          initial={{ width: 0 }}
          animate={{ 
            width: `${(currentStepIndex / (steps.length - 1)) * 100}%` 
          }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
        />

        {steps.map((step, index) => {
          const isCompleted = index <= currentStepIndex;
          const isCurrent = index === currentStepIndex;
          const StepIcon = step.icon;

          return (
            <motion.div
              key={step.key}
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: index * 0.15 }}
              className="relative z-10 flex flex-col items-center"
            >
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                  isCompleted
                    ? 'bg-discount text-discount-foreground'
                    : 'bg-secondary text-muted-foreground'
                } ${isCurrent ? 'ring-4 ring-discount/20' : ''}`}
              >
                {isCompleted && !isCurrent ? (
                  <Check className="h-4 w-4" />
                ) : (
                  <StepIcon className="h-4 w-4" />
                )}
              </div>
              <span
                className={`text-xs mt-2 font-medium whitespace-nowrap ${
                  isCompleted ? 'text-foreground' : 'text-muted-foreground'
                }`}
              >
                {step.label}
              </span>
            </motion.div>
          );
        })}
      </div>

      {/* Estimated delivery */}
      {status !== 'delivered' && (
        <p className="text-center text-sm text-muted-foreground mt-4">
          Estimated delivery: <span className="font-medium text-foreground">28 Dec - 30 Dec</span>
        </p>
      )}
    </div>
  );
}
